<template>
  <div class="left-menu-wrapper">
    <!-- Mobile Filter Toggle Button (only if not in modal) -->
    <button
      v-if="!hideToggle"
      @click="toggleMobileMenu"
      class="mobile-filter-toggle md:hidden"
      :class="{ active: showMobileMenu }"
    >
      <i class="mdi mdi-filter-variant"></i>
      <span>{{ showMobileMenu ? "Hide Menu" : "Show Menu" }}</span>
    </button>

    <!-- Con el menu oculto queda esta franja para traerlo de vuelta: sin ella
         no habria forma de recuperarlo. Solo en escritorio; en movil el menu ya
         tiene su propio toggle arriba. -->
    <button
      v-if="collapsible && collapsed"
      class="sub-menu-reopen"
      title="Show menu"
      @click="setCollapsed(false)"
    >
      <i class="mdi mdi-chevron-right"></i>
    </button>

    <!-- Left Menu (collapsible on mobile, always visible in modal or desktop) -->
    <div
      class="left-menu"
      :class="{
        'mobile-hidden': !hideToggle && !showMobileMenu,
        'desktop-collapsed': collapsible && collapsed
      }"
    >
      <button
        v-if="collapsible"
        class="sub-menu-collapse"
        title="Hide menu"
        @click="setCollapsed(true)"
      >
        <i class="mdi mdi-chevron-left"></i>
      </button>
      <ul class="nav-list">
        <li v-if="searchEnabled || $slots.default || $slots.searchActions">
          <slot />
          <div class="flex flex-col items-center">
            <input
              type="text"
              v-model="search"
              v-if="searchEnabled"
              class="border border-gray rounded-md p-2 flex-1 mb-2"
              placeholder="Search"
              @keyup.enter="handleEnter"
            />
            <slot name="searchActions" />
          </div>
        </li>
        <li
          v-for="(item, index) in menuItems"
          :key="index"
          class="nav-item noselect"
          :class="{ selected: item.value === selectedStatus }"
          @click="updateStatusFilter(item.value)"
        >
          <span class="nav-item-text">{{ item.displayText }}</span>
          <span
            v-if="item.count !== null && item.count !== undefined"
            class="nav-item-count"
            :class="{ 'count-alert': item.count > 0 }"
          >
            {{ item.count }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    menuItems: {
      type: Array,
      required: true
    },
    selectedStatus: {
      type: [String, Boolean],
      required: false,
      default: ""
    },
    searchProp: {
      type: String,
      required: false,
      default: ""
    },
    searchEnabled: {
      type: Boolean,
      default: true
    },
    hideToggle: {
      type: Boolean,
      default: false
    },
    // Poder plegar el menu en escritorio para darle la pantalla al contenido.
    collapsible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      search: this.searchProp || "",
      showMobileMenu: false,
      collapsed: this.readCollapsed()
    };
  },
  watch: {
    searchProp(val) {
      this.search = val;
    },
    search(val) {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.$emit("searchUpdated", val);
      }, 300);
    }
  },
  methods: {
    updateStatusFilter(status) {
      this.$emit("selectedStatusFilter", status);
      // Auto-hide menu on mobile after selection
      if (window.innerWidth < 768) {
        this.showMobileMenu = false;
      }
    },
    handleEnter() {
      this.$emit("searchEnter");
    },
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
    },
    // La preferencia se guarda por pantalla: plegar el menu en Marketplaces no
    // tiene por que plegarlo en Ordenes, donde el filtro se usa todo el tiempo.
    storageKey() {
      const route = (this.$route && this.$route.name) || "default";
      return `leftSubMenuCollapsed:${route}`;
    },
    readCollapsed() {
      try {
        return window.localStorage.getItem(this.storageKey()) === "1";
      } catch (e) {
        // Navegador con el almacenamiento bloqueado: el menu arranca visible.
        return false;
      }
    },
    setCollapsed(value) {
      this.collapsed = value;
      this.$emit("collapsedChange", value);
      try {
        window.localStorage.setItem(this.storageKey(), value ? "1" : "0");
      } catch (e) {
        // Sin persistencia, el plegado dura lo que la vista.
      }
    }
  }
};
</script>
<style lang="scss">
@import "@/assets/style/css/generic-variables.scss";

.left-menu-wrapper {
  display: contents;

  @media screen and (max-width: 768px) {
    display: block;
    width: 100%;
  }
}

.mobile-filter-toggle {
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 20px;
    background-color: $subSidenavBackgroundColor;
    color: $text-dark-1;
    border: none;
    border-bottom: 2px solid $M6;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
      background-color: $M6;
    }

    &.active {
      background-color: $M6;
      border-bottom-color: $link-color-1;

      i {
        transform: rotate(180deg);
      }
    }

    i {
      font-size: 22px;
      transition: transform 0.2s ease;
      color: $link-color-1;
    }
  }
}

/* Ambos controles son de escritorio: en movil manda el toggle de arriba. */
.sub-menu-collapse {
  display: none;

  @media screen and (min-width: 769px) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding: 4px 8px;
    background-color: transparent;
    border: none;
    border-bottom: 1px solid black;
    color: $subSidenavFontColor;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }

    i {
      font-size: 20px;
    }
  }
}

.sub-menu-reopen {
  display: none;

  @media screen and (min-width: 769px) {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    align-self: stretch;
    width: 24px;
    min-width: 24px;
    padding-top: 6px;
    background-color: $subSidenavBackgroundColor;
    color: $subSidenavFontColor;
    border: none;
    cursor: pointer;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.35);

    &:hover {
      background-color: $subSidenavHover;
    }

    i {
      font-size: 20px;
    }
  }
}

.left-menu {
  color: $subSidenavFontColor;
  background-color: $subSidenavBackgroundColor;
  width: 255px;
  min-width: 255px;
  font-weight: 700;
  font-size: 12px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.35);
  transition: all 0.3s ease;

  @media screen and (min-width: 769px) {
    &.desktop-collapsed {
      display: none;
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    box-shadow: none;
    font-size: 14px;
    margin-bottom: 0;
    overflow: hidden;

    &.mobile-hidden {
      max-height: 0;
      opacity: 0;
      visibility: hidden;
    }

    &:not(.mobile-hidden) {
      max-height: 80vh;
      opacity: 1;
      visibility: visible;
      overflow-y: auto;
    }
  }

  ul {
    margin-top: 0px;
    padding: 0px;
    list-style: none;

    li {
      padding: 10px 25px;
      width: 100%;
      border-bottom: 1px solid black;
      cursor: pointer;
      font-size: 17px;
      font-weight: 400;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .nav-item-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .nav-item-count {
        margin-left: 8px;
        flex-shrink: 0;
        color: inherit;

        &.count-alert {
          color: #dc2626;
          font-weight: 700;
        }
      }

      @media screen and (max-width: 768px) {
        padding: 14px 20px;
        font-size: 15px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        &:first-child {
          border-top: none;
        }

        &:last-child {
          border-bottom: none;
        }
      }

      input {
        width: 100%;
        border-radius: 6px;

        @media screen and (max-width: 768px) {
          padding: 10px 12px;
          font-size: 16px; /* Prevent zoom on iOS */
        }
      }

      &.nav-item:not(.selected):hover {
        background-color: $subSidenavHover;
        color: $subSidenavFontColor;
      }

      &.selected {
        background-color: $subSidenavSelected;
        -moz-box-shadow: inset 0 0 5px #000000;
        -webkit-box-shadow: inset 0 0 5px #000000;
        box-shadow: inset 0 0 5px #000000;
        color: $subSidenavFontColorSelected;

        @media screen and (max-width: 768px) {
          border-left: 4px solid $link-color-1;
          padding-left: 16px;
        }
      }

      /* Touch-friendly targets on mobile */
      @media screen and (max-width: 768px) {
        &.nav-item {
          min-height: 48px;
          display: flex;
          align-items: center;
        }
      }
    }
  }
}
</style>
